import 'mana-font/css/mana.css';

interface ManaSymbolProps {
    symbol: string; // e.g., "W", "U", "B", "R", "G", "0", "1", "21", "X", "C", "S", etc.
    size?: 'sm' | 'md' | 'lg' | 'xl';
    className?: string;
}

// Map common mana symbols to mana-font classes.
// NOTE: Numeric classes (0-20) are intentionally constructed dynamically in ManaSymbol()
// rather than written as static string literals so Tailwind CSS's JIT scanner
// does not mistake them for logical margin-inline-start utilities.
const symbolMap: Record<string, string> = {
    // Basic mana
    'W': 'ms-w',      // White
    'U': 'ms-u',      // Blue
    'B': 'ms-b',      // Black
    'R': 'ms-r',      // Red
    'G': 'ms-g',      // Green
    'C': 'ms-c',      // Colorless
    'S': 'ms-s',      // Snow
    'X': 'ms-x',      // Variable
    'Y': 'ms-y',      // Variable (Y)
    'Z': 'ms-z',      // Variable (Z)

    // Phyrexian mana
    'WP': 'ms-wp',
    'UP': 'ms-up',
    'BP': 'ms-bp',
    'RP': 'ms-rp',
    'GP': 'ms-gp',
    'P': 'ms-p',

    // Hybrid mana
    'WU': 'ms-wu',
    'WB': 'ms-wb',
    'UB': 'ms-ub',
    'UR': 'ms-ur',
    'BR': 'ms-br',
    'BG': 'ms-bg',
    'RG': 'ms-rg',
    'RW': 'ms-rw',
    'GW': 'ms-gw',
    'GU': 'ms-gu',

    // 2/color hybrid
    '2W': 'ms-2w',
    '2U': 'ms-2u',
    '2B': 'ms-2b',
    '2R': 'ms-2r',
    '2G': 'ms-2g',

    // Special
    'T': 'ms-tap',
    'Q': 'ms-untap',
    'E': 'ms-e',      // Energy
    'CHAOS': 'ms-chaos',
    'HALF': 'ms-half',
    'INFINITY': 'ms-infinity',
};

const sizeClasses: Record<string, string> = {
    'sm': 'ms-cost',      // ~0.95em
    'md': 'ms-cost ms-2x', // 1.75em
    'lg': 'ms-cost ms-3x', // 2.25em
    'xl': 'ms-cost ms-4x', // 3em
};

const fallbackSizeFontEm: Record<NonNullable<ManaSymbolProps['size']>, string> = {
    'sm': '0.95em',
    'md': '1.75em',
    'lg': '2.25em',
    'xl': '3em',
};

export function ManaSymbol({ symbol, size = 'sm', className = '' }: ManaSymbolProps) {
    const trimmed = String(symbol ?? '').trim();
    const sizeClass = sizeClasses[size] || sizeClasses.sm;

    // Handle pure numeric mana values (0..20 via mana-font; 21+ via custom circular mana badge)
    if (/^\d+$/.test(trimmed)) {
        const numericValue = parseInt(trimmed, 10);

        if (numericValue <= 20 || numericValue === 1000000) {
            const numClass = ['ms', String(numericValue)].join('-');
            return (
                <i
                    className={`ms ${numClass} ${sizeClass} ${className}`}
                    style={{ marginInlineStart: 0 }}
                    aria-label={trimmed}
                />
            );
        }

        // mana-font has no glyphs for 21+ (renders a blank grey circle).
        // Render an exact-sized #beb9b2 mana circle with MPlantin numerals matching ms-cost.
        const digitFontSize = trimmed.length >= 3 ? '0.56em' : '0.76em';
        return (
            <span
                className={`inline-flex items-center justify-center rounded-full select-none flex-shrink-0 not-italic ${className}`}
                style={{
                    fontSize: fallbackSizeFontEm[size] || '0.95em',
                    width: '1.3em',
                    height: '1.3em',
                    backgroundColor: '#beb9b2',
                    color: '#111111',
                    verticalAlign: 'middle',
                    lineHeight: 1,
                    marginInlineStart: 0,
                }}
                aria-label={trimmed}
            >
                <span
                    style={{
                        fontSize: digitFontSize,
                        fontFamily: 'MPlantin, Georgia, "Times New Roman", serif',
                        fontWeight: 700,
                        letterSpacing: '-0.04em',
                        lineHeight: 1,
                        transform: 'translateY(0.03em)',
                    }}
                >
                    {trimmed}
                </span>
            </span>
        );
    }

    const normalized = trimmed.replace(/\//g, '').toUpperCase();
    const symbolClass = symbolMap[normalized] || ['ms', normalized.toLowerCase()].join('-');

    return (
        <i
            className={`ms ${symbolClass} ${sizeClass} ${className}`}
            style={{ marginInlineStart: 0 }}
            aria-label={trimmed}
        />
    );
}

// Parse a mana cost string like "{2}{W}{U}" into symbols
export function parseManaString(manaString: string): string[] {
    const matches = manaString.match(/\{([^}]+)\}/g);
    if (!matches) return [];
    return matches.map(m => m.replace(/[{}]/g, ''));
}

// Render a full mana cost
interface ManaCostProps {
    cost: string; // e.g., "{2}{W}{U}" or "2WU"
    size?: 'sm' | 'md' | 'lg' | 'xl';
    className?: string;
}

export function ManaCost({ cost, size = 'sm', className = '' }: ManaCostProps) {
    // Handle both "{2}{W}{U}" and "2WU" formats
    let symbols: string[];

    if (cost.includes('{')) {
        symbols = parseManaString(cost);
    } else {
        // Parse simple format like "2WU"
        symbols = [];
        let i = 0;
        while (i < cost.length) {
            // Check for multi-digit numbers
            if (/\d/.test(cost[i])) {
                let num = '';
                while (i < cost.length && /\d/.test(cost[i])) {
                    num += cost[i];
                    i++;
                }
                symbols.push(num);
            } else {
                symbols.push(cost[i]);
                i++;
            }
        }
    }

    return (
        <span className={`inline-flex items-center gap-0.5 ${className}`}>
            {symbols.map((symbol, idx) => (
                <ManaSymbol key={idx} symbol={symbol} size={size} />
            ))}
        </span>
    );
}

export default ManaSymbol;
